// Types for calendar service
interface CalendarEvent {
  id: string;
  title: string;
  startTime: Date;
  endTime: Date;
  location?: string;
  description?: string;
  attendees?: string[];
}

type CalendarProvider = 'google' | 'outlook' | 'icloud';

// Mock calendar service for frontend
const calendarService = {
  // Get connected calendars
  async getConnectedCalendars() {
    // Get user email from localStorage
    const userEmail = localStorage.getItem('userEmail');
    try {
      // Build URL with email parameter if available
      const url = userEmail ? 
        `https://salon.rockyroadsolutions.com/api/calendar/connected?email=${encodeURIComponent(userEmail)}` : 
        'https://salon.rockyroadsolutions.com/api/calendar/connected';
      
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching connected calendars:', error);
      // Get user email from localStorage
      const userEmail = localStorage.getItem('userEmail') || 'user';
      const username = userEmail.split('@')[0];
      
      // Return mock data as fallback with user's email
      return [
        {
          id: 'google-1',
          provider: 'google',
          email: `${username}@gmail.com`,
          connected: true,
          lastSynced: new Date()
        },
        {
          id: 'outlook-1',
          provider: 'outlook',
          email: `${username}@outlook.com`,
          connected: false
        },
        {
          id: 'icloud-1',
          provider: 'icloud',
          email: `${username}@icloud.com`,
          connected: false
        }
      ];
    }
  },

  // Connect calendar
  async connectCalendar(provider: CalendarProvider) {
    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/calendar/connect/${provider}`);
      const data = await response.json();
      
      // In a real implementation, this would redirect to the OAuth URL
      console.log(`Auth URL for ${provider}:`, data.authUrl);
      
      // Get user's email from auth context or localStorage
      let userEmail = localStorage.getItem('userEmail') || '';
      
      // If no email is found, try to get it from the auth context
      if (!userEmail) {
        try {
          const userDataStr = localStorage.getItem('userData');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            if (userData && userData.email) {
              userEmail = userData.email;
              localStorage.setItem('userEmail', userEmail);
            }
          }
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      
      // Use the actual email or fallback to a generic one
      return {
        success: true,
        provider,
        email: userEmail || `user@${provider}.com`
      };
    } catch (error) {
      console.error(`Error connecting to ${provider} calendar:`, error);
      throw error;
    }
  },

  // Disconnect calendar
  async disconnectCalendar(provider: CalendarProvider) {
    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/calendar/disconnect/${provider}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return await response.json();
    } catch (error) {
      console.error(`Error disconnecting ${provider} calendar:`, error);
      throw error;
    }
  },

  // Add event to calendar
  async addEvent(provider: CalendarProvider, event: CalendarEvent) {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/calendar/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider, event })
      });
      return await response.json();
    } catch (error) {
      console.error('Error adding event to calendar:', error);
      throw error;
    }
  }
};

export default calendarService;
